import { type ReactNode, useState } from "react";
import { Lock } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { useDemoMode } from "@/components/DemoBanner";
import { readSalesPresenterCookie } from "@/lib/salesPresenter";
import { DEMO_BOOKING_URL, trackDemoCtaClick } from "@/lib/demoBooking";

/**
 * Demo IP lockdown — client export gate (batch A / D2).
 *
 * The persisted demo workspace lets any prospect browse in-app, but they must
 * not walk away with a concrete artifact (PDF/CSV/XLSX/report). This component
 * wraps a real export trigger and, when the visitor is in the demo workspace
 * AND is NOT a Zindeal rep presenting live, replaces the trigger with a muted
 * locked button that opens a booking-conversion modal instead of downloading.
 *
 * The server-side `blockDemoExports` middleware is the hard guarantee (every
 * export endpoint 403s for demo visitors); this component is the conversion
 * surface — it turns a dead-end 403 into a "book your onboarding" prompt.
 *
 * Locked visual is intentionally professional: slate/muted, a small lock icon,
 * subtle copy. Never red, never accusatory — this is a sales tool.
 *
 * Usage: wrap the existing export control unchanged. When unlocked, `children`
 * render exactly as before.
 *
 *   <DemoExportGate label="Export Report">
 *     <Button onClick={handleExport}>Export Report</Button>
 *   </DemoExportGate>
 */

/** True when the current viewer is a demo visitor with no live-rep presenter cookie. */
export function useDemoExportLocked(): boolean {
  const isDemo = useDemoMode();
  // The signed httpOnly `sales_presenter` cookie is the server source of truth;
  // this reads the JS-visible mirror only to shape the UI. The server gate
  // still enforces the real policy, so a spoofed mirror cookie only changes
  // which button renders — the export itself stays 403'd server-side.
  const presenter = typeof document !== "undefined" && readSalesPresenterCookie().active;
  return isDemo && !presenter;
}

export interface DemoExportGateProps {
  /** The real export control, rendered unchanged when the gate is open. */
  children: ReactNode;
  /** Label shown on the locked stand-in button. */
  label?: string;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  className?: string;
  /** test id for the locked stand-in button (defaults to `demo-export-locked`). */
  lockedTestId?: string;
}

export function DemoExportGate({
  children,
  label = "Export",
  size = "sm",
  variant = "outline",
  className,
  lockedTestId = "demo-export-locked",
}: DemoExportGateProps) {
  const locked = useDemoExportLocked();
  const [open, setOpen] = useState(false);

  if (!locked) return <>{children}</>;

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size={size}
              variant={variant}
              className={`text-slate-500 border-slate-200 hover:text-slate-700 ${className ?? ""}`}
              onClick={() => setOpen(true)}
              data-testid={lockedTestId}
              aria-label={`${label} (locked in demo)`}
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" />
              {label}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Exports unlock after your onboarding call.</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="demo-export-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100">
                <Lock className="h-3.5 w-3.5 text-indigo-600" />
              </span>
              This is a preview workspace.
            </DialogTitle>
            <DialogDescription className="pt-1 leading-relaxed">
              Live PDF exports, term sheet downloads, and report generation
              unlock after your onboarding call. That's a 30-minute walkthrough
              where we tailor Zindeal to your program.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              data-testid="demo-export-continue"
            >
              Continue exploring
            </Button>
            <Button
              asChild
              className="bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="demo-export-book"
            >
              <a
                href={DEMO_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDemoCtaClick("demo_export_gate")}
              >
                Book onboarding →
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
